import React, { useEffect, useState } from 'react';
import { User,Mail,Phone,Save,X,Clock,CheckCircle,XCircle,AlertCircle,FileText,Home } from 'lucide-react';
import axios from 'axios';
import { BASEURL } from '../constants';
import SuburbSearch from './SuburbSearch';
import { toast } from 'react-toastify';

function EditStudentDetails() {
  const [formData, setFormData] = useState({});
  const [editRequest, setEditRequest] = useState(null);
  const [originalData, setOriginalData] = useState({});
  const [cancellingRequest, setCancellingRequest] = useState(false); 
  const studentId = localStorage.getItem("studentId");
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const copyResidentialToMailing = () => {
    setFormData(prev => ({
      ...prev,
      mailingAddressLine1: `${prev.flatDetail} ${prev.streetNo} ${prev.streetName}`.trim(), 
      mailingSuburb: prev.localSuburb,
      mailingState: prev.localState,
      mailingPostCode: prev.localPostCode,
      mailingCountry: prev.localCountry,
    }));
  };

  const handleSubmitRequest = async () => {
    const studentId = localStorage.getItem("studentId");
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Check if there are any changes
    if (!hasChanges()) {
      toast.error("No changes detected. Please modify at least one field before submitting.");
      return;
    }

    // Check if there's already a pending request
    if (editRequest && editRequest.status === 'pending') {
      toast.error("You already have a pending request. Please wait for it to be processed.");
      return;
    }

    try {
      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dob: convertToISODate(formData.dob),
        fax: formData.fax || "",
        mobile: formData.mobile,
        email: formData.email,
        localAddressLine1: formData.localAddressLine1 || "",
        localAddressLine2: formData.localAddressLine2 || "",
        localAddressLine3: formData.localAddressLine3 || "",
        mailingAddressLine1: formData.mailingAddressLine1 || "",
        mailingAddressLine2: formData.mailingAddressLine2 || "",
        mailingAddressLine3: formData.mailingAddressLine3 || "",
        lSuburbID: formData.localSuburbID || 0,
        lSuburb: formData.localSuburb || "",
        lState: formData.localState || "",
        lPostCode: formData.localPostCode || "",
        lCountry: formData.localCountry || "",
        mSuburbID: formData.mailingSuburbID || 0,
        mSuburb: formData.mailingSuburb || "",
        mState: formData.mailingState || "",
        mPostCode: formData.mailingPostCode || "",
        mCountry: formData.mailingCountry || "",
        avetmissSuburbID: formData.avetmissSuburbID || 0,
        propertyName: formData.propertyName || "",
        flatDetail: formData.flatDetail || "",
        streetName: formData.streetName || "",
        streetNo: formData.streetNo || ""
      };

      const response = await axios.post(
        `${BASEURL}Student/ChangeStudentInformation/${studentId}`,
        requestData
      );
      
      setEditRequest({
        id: response.data.studentChangeInformationId,
        submittedDate: new Date(response.data.createDate).toLocaleDateString('en-AU'),
        status: 'pending'
      });

      toast.success("Request submitted successfully!");
      checkExistingRequest()
    } catch (error) {
      console.error("Error submitting request:", error);
      
      if (error.response?.status === 400) {
        toast.error(error.response?.data || "You already have one pending request");
      } else {
        toast.error("Failed to submit request. Please try again.");
      }
    }
  };

  const checkExistingRequest = async () => {
    try {
      const response = await axios.get(
        `${BASEURL}Student/ChangeStudentInformation/LastRequest/${studentId}`
      );
      
      // If there's an active pending request (status = 0)
      if (response?.data && response.data.status === 0 && response.data.active === true) {
        setEditRequest({
          id: response.data.studentChangeInformationId,
          status: 'pending',
          submittedDate: new Date(response.data.createDate).toLocaleDateString('en-AU')
        });
        return response.data;
      }
      return null;
    } catch (error) {
      // 404 means no request exists, which is fine
      if (error.response?.status !== 404) {
        console.error("Error checking existing request:", error);
      }
      return null;
    }
  };

  const loadStudentData = async () => {
  // setLoading(true);
  try {
    const pendingData = await checkExistingRequest();
    
    if (pendingData !== null) {
      // Pending request exists - use that data
      const studentInfo = await fetchStudentInfo(true);
      if (studentInfo) {
        setFormData(studentInfo);
        setOriginalData(studentInfo);
      }
    } else {
      // No pending request - use student info
      const studentInfo = await fetchStudentInfo(false);
      if (studentInfo) {
        setFormData(studentInfo);
        setOriginalData(studentInfo);
      }
    }
  } catch (error) {
    console.error("Error loading data:", error);
  }
};

  const convertToISODate = (dateString) => {
    if (!dateString) return null;
    
    // If already in ISO format, return as is
    if (dateString.includes('-')) {
      return dateString;
    }
    
    // Convert from DD/MM/YYYY to YYYY-MM-DD
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return null;
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          label: 'Pending Review'
        };
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Approved'
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Rejected'
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Unknown'
        };
    }
  };

  const fetchStudentInfo = async (isPending) => {
    const studentId = localStorage.getItem("studentId");
    try {
      const data = await axios.get(isPending?`${BASEURL}Student/StudentChangeRequest/LastRequest/${studentId}`:`${BASEURL}Student/Address/${studentId}`);
      return data?.data[0]
      // setFormData(data?.data[0]);
      // setOriginalData(data?.data[0]);
    } catch (error) {
      console.error("Error while fetching the student info", error);
      return null;
    }
  }

  const hasChanges = () => {
  if (!originalData || Object.keys(originalData).length === 0) return false;
  
  const fieldsToCheck = [
    'firstName', 'lastName', 'dob', 'email', 'mobile', 'fax',
    'localAddressLine1', 'localAddressLine2', 'localAddressLine3',
    'localSuburbID', 'localSuburb', 'localState', 'localPostCode', 'localCountry',
    'mailingAddressLine1', 'mailingAddressLine2', 'mailingAddressLine3',
    'mailingSuburbID', 'mailingSuburb', 'mailingState', 'mailingPostCode', 'mailingCountry',
    'avetmissSuburbID', 'propertyName', 'flatDetail', 'streetName', 'streetNo'
  ];
  
  return fieldsToCheck.some(field => {
    const original = originalData[field] || '';
    const current = formData[field] || '';
    return original !== current;
  });
};

  useEffect(() => {
    // fetchStudentInfo();
    // checkExistingRequest();
    loadStudentData();
  }, [])

  const statusConfig = editRequest ? getStatusConfig(editRequest.status) : null;
  const StatusIcon = statusConfig?.icon;
  const hasPendingRequest = editRequest && editRequest.status === 'pending';
  const isSubmitDisabled = hasPendingRequest || !hasChanges();

  async function handleCancelRequest() {
    try {
      setCancellingRequest(true);
      await axios.get(`${BASEURL}student/CancelChangeRequest/${studentId}/${editRequest.id}`)
      setEditRequest(null);
      toast.success("Request cancelled successfully!")
      await loadStudentData();
    } catch (error) {
      console.error("Error while canceling the request.", error)
      toast.error("Failed to cancel request. Please try again.");
    }
  }

  return (
    <div className="">
      {/* Status Banner - Only show if there's a request */}
      {editRequest && (
        <div className={`bg-white rounded-lg shadow-sm border-2 ${statusConfig.borderColor} overflow-hidden my-6`}>
          <div className={`${statusConfig.bgColor} px-6 py-4 border-b ${statusConfig.borderColor}`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
                <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color} border ${statusConfig.borderColor}`}>
                    {statusConfig.label.toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-600 text-sm">
                  {/* Request ID: <span className="font-medium text-gray-900">#{editRequest.id}</span> •  */}
                  Submitted on {editRequest.submittedDate}
                </p>
              </div>
              
              <button type="button" onClick={handleCancelRequest} className="rounded-lg text-sm px-4 py-2 border border-red-300 text-red-600 hover:bg-red-100 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/20">
              Cancel Request
              </button> 
            </div>
          </div>
          
          {hasPendingRequest && (
            <div className="px-6 py-3 bg-white border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-amber-700">
                <AlertCircle className="w-4 h-4" />
                <span>Your request is being reviewed. You cannot submit a new request until this one is processed.</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 space-y-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-gray-900">Edit Student Details</h2>
            <p className="text-gray-600 text-sm">Submit a request to update your information</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              All changes require administrative approval.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
          {/* Personal Information */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-1">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-blue-600" />
                <p className="text-gray-900">Personal Information</p>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Date of Birth *</label>
                  <input
                    type="text"
                    value={formData.dob}
                    onChange={(e) => handleInputChange('dob', e.target.value)}
                    disabled={hasPendingRequest}
                    placeholder="DD/MM/YYYY"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div> */}

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-blue-600" />
                <p className="text-gray-900">Contact Information</p>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email || ""}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Mobile Number *</label>
                  <input
                    type="tel"
                    value={formData.mobile || ""}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="04XX XXX XXX"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* Residential Address */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 ">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-blue-600" />
                <p className="text-gray-900">Residential Address</p>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-2">Address Line 1 *</label>
                  <input
                    type="text"
                    value={formData.localAddressLine1 || ""}
                    onChange={(e) => handleInputChange('localAddressLine1', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Street address"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-2">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.localAddressLine2 || ""}
                    onChange={(e) => handleInputChange('localAddressLine2', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Apartment, suite, etc."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-2">Address Line 3</label>
                  <input
                    type="text"
                    value={formData.localAddressLine3 || ""}
                    onChange={(e) => handleInputChange('localAddressLine3', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Additional address information"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Suburb *</label>
                  <SuburbSearch
                    value={formData.localSuburb || ""}
                    placeholder="Search suburb"
                    disabled={hasPendingRequest}
                    onSelect={(data) => {
                      setFormData(prev => ({
                        ...prev,
                        localSuburbID: data.suburbID,
                        localSuburb: data.suburb,
                        localState: data.state,
                        localPostCode: data.postCode,
                        localCountry: data.country
                      }))
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">State *</label>
                  <input
                    type="text"
                    value={formData.localState || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Post Code *</label>
                  <input
                    type="text"
                    value={formData.localPostCode || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Country *</label>
                  <input
                    type="text"
                    value={formData.localCountry || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mailing Address */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <p className="text-gray-900">Mailing Address</p>
                </div>
                <button
                  type="button"
                  onClick={copyResidentialToMailing}
                  disabled={hasPendingRequest}
                  className="px-3 py-1 bg-blue-50 text-blue-600 rounded text-sm hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Copy Residential
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-2">Address Line 1</label>
                  <input
                    type="text"
                    value={formData.mailingAddressLine1 || ""}
                    onChange={(e) => handleInputChange('mailingAddressLine1', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Street address"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-2">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.mailingAddressLine2 || ""}
                    onChange={(e) => handleInputChange('mailingAddressLine2', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Apartment, suite, etc."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-2">Address Line 3</label>
                  <input
                    type="text"
                    value={formData.mailingAddressLine3 || ""}
                    onChange={(e) => handleInputChange('mailingAddressLine3', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Additional information"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Suburb</label>
                  <SuburbSearch
                    value={formData.mailingSuburb || ""}
                    placeholder="Search suburb"
                    disabled={hasPendingRequest}
                    onSelect={(data) => {
                      setFormData(prev => ({
                        ...prev,
                        mailingSuburbID: data.suburbID,
                        mailingSuburb: data.suburb,
                        mailingState: data.state,
                        mailingPostCode: data.postCode,
                        mailingCountry: data.country
                      }))
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    value={formData.mailingState || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Post Code</label>
                  <input
                    type="text"
                    value={formData.mailingPostCode || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Country</label>
                  <input
                    type="text"
                    value={formData.mailingCountry || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* AVETMISS Address */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-gray-900">AVETMISS Address</p>
                    <p className="text-xs text-gray-600 mt-0.5">Official VET reporting address</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Property Name</label>
                  <input
                    type="text"
                    value={formData.propertyName || ""}
                    onChange={(e) => handleInputChange('propertyName', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Building name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Flat/Unit Detail</label>
                  <input
                    type="text"
                    value={formData.flatDetail || ""}
                    onChange={(e) => handleInputChange('flatDetail', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., Suite 30"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Street Number *</label>
                  <input
                    type="text"
                    value={formData.streetNo || ""}
                    onChange={(e) => handleInputChange('streetNo', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., 60"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Street Name *</label>
                  <input
                    type="text"
                    value={formData.streetName || ""}
                    onChange={(e) => handleInputChange('streetName', e.target.value)}
                    disabled={hasPendingRequest}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., Central Street"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Suburb *</label>
                  <SuburbSearch
                    value={formData.avetmissSuburb || ""}
                    placeholder="Search suburb"
                    disabled={hasPendingRequest}
                    onSelect={(data) => {
                      setFormData(prev => ({
                        ...prev,
                        avetmissSuburbID: data.suburbID,
                        avetmissSuburb: data.suburb,
                        avetmissState: data.state,
                        avetmissPostCode: data.postCode,
                        avetmissCountry: data.country
                      }))
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">State *</label>
                  <input
                    type="text"
                    value={formData.avetmissState || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Post Code *</label>
                  <input
                    type="text"
                    value={formData.avetmissPostCode || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Country *</label>
                  <input
                    type="text"
                    value={formData.avetmissCountry || ""}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white flex justify-center">
            <div className="flex flex-col sm:flex-row gap-3 w-60">
              <button
                onClick={handleSubmitRequest}
                disabled={hasPendingRequest}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                Submit Request
              </button>
              {/* <button
                onClick={() => fetchStudentInfo()}
                disabled={hasPendingRequest}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
                Reset
              </button> */}
            </div>
          </div>
      </div>
    </div>
  );
}

export default EditStudentDetails;